import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { usePageMeta } from '../../../hooks/usePageMeta';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../../components/2025/Header';
import Footer from '../../../components/2025/Footer';
import FloatingNav from '../../../components/2025/FloatingNav';
import HeroTitle from '../../../components/2025/HeroTitle';
import EventCard from '../../../components/2025/EventCard';
import EventFilterNav from '../../../components/2025/events/EventFilterNav';
import LoadingSpinner from '../../../components/2025/LoadingSpinner';
import { fetchAndCategorizeEvents, CategorizedEvents } from '../../../services/eventService';
import { event } from '../../../types/cms-types';
import { optimizeImage } from '../../../utils/imageOptimizer';
import Styles from './styles.module.scss';

const convertEventToEventData = (event: event) => ({
  imageUrl: optimizeImage(event.event_image.url, { width: 800, quality: 80 }),
  title: event.event_title,
  time: event.event_time,
  location: event.event_location,
  description: event.event_description,
  labels: [
    ...event.event_category,
    ...event.event_day,
    ...(event.school || []),
    ...(event.faculties || []),
    ...(event.department || []),
  ],
});

const Events2025: React.FC = () => {
  const [categorizedEvents, setCategorizedEvents] = useState<CategorizedEvents | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<event | null>(null);
  const scrollPositionRef = useRef<number | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  usePageMeta({
    title: 'イベント一覧 - 全ての企画',
    description:
      'かまた祭2025のイベント情報一覧。各種ステージイベント、サークル・有志によるイベント、特別イベントなど、盛りだくさんのプログラムをご紹介します。',
  });

  // イベントを選択する関数
  const handleSelectEvent = (event: event) => {
    setSelectedEvent(event);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('event', event.id);
    navigate(`?${newSearchParams.toString()}`, { replace: true });
  };

  // モーダルを閉じる関数
  const handleCloseModal = () => {
    const savedScrollY = scrollPositionRef.current;

    setSelectedEvent(null);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('event');
    const paramString = newSearchParams.toString();
    navigate(paramString ? `?${paramString}` : '', { replace: true });

    requestAnimationFrame(() => {
      const body = document.body;
      const html = document.documentElement;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      html.style.overflow = '';

      if (savedScrollY !== null) {
        window.scrollTo(0, savedScrollY);
        scrollPositionRef.current = null;
      }
    });
  };

  // URLクエリからイベントを開く
  useEffect(() => {
    const eventId = searchParams.get('event');
    if (eventId && categorizedEvents) {
      const allEvents = Object.values(categorizedEvents).flat();
      const foundEvent = allEvents.find((e) => e.id === eventId);
      if (foundEvent) {
        setSelectedEvent(foundEvent);
      }
    }
  }, [searchParams, categorizedEvents]);

  // モーダルの開閉に応じてbodyのスクロールを制御
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const body = document.body;
    const html = document.documentElement;

    if (selectedEvent) {
      if (scrollPositionRef.current === null) {
        const scrollY = window.scrollY || html.scrollTop || 0;
        scrollPositionRef.current = scrollY;

        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';
      }
    }
  }, [selectedEvent]);

  // 重複を排除した全イベントリスト
  const allUniqueEvents = useMemo(() => {
    if (!categorizedEvents) return [];
    const uniqueEventsMap = new Map<string, event>();
    Object.values(categorizedEvents)
      .flat()
      .forEach((event) => {
        if (!uniqueEventsMap.has(event.id)) {
          uniqueEventsMap.set(event.id, event);
        }
      });
    return Array.from(uniqueEventsMap.values());
  }, [categorizedEvents]);

  // 日付ごとにグループ化したイベント
  const eventsByDate = useMemo(() => {
    const grouped: { [date: string]: event[] } = {};
    allUniqueEvents.forEach((event) => {
      event.event_day.forEach((day) => {
        if (!grouped[day]) {
          grouped[day] = [];
        }
        if (!grouped[day].find((e) => e.id === event.id)) {
          grouped[day].push(event);
        }
      });
    });
    const sortedDates = Object.keys(grouped).sort((a, b) => {
      const dateA = a.split('/').map(Number);
      const dateB = b.split('/').map(Number);
      if (dateA[0] !== dateB[0]) return dateA[0] - dateB[0];
      return dateA[1] - dateB[1];
    });
    const result: { date: string; events: event[] }[] = sortedDates.map((date) => ({
      date,
      events: grouped[date],
    }));
    return result;
  }, [allUniqueEvents]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setIsLoading(true);
        const events = await fetchAndCategorizeEvents();
        setCategorizedEvents(events);
        setError(null);
      } catch (err) {
        console.error('イベントデータの取得に失敗しました:', err);
        setError('イベントデータの読み込みに失敗しました');
      } finally {
        setIsLoading(false);
      }
    };

    loadEvents();
  }, []);

  return (
    <>
      <Header />
      <main className={Styles.events}>
        <HeroTitle title="EVENT" subtitle="イベント" />
        <EventFilterNav />

        {isLoading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className={Styles.error}>
            <p>{error}</p>
          </div>
        ) : (
          <div className={Styles.eventSections}>
            {/* 各カテゴリのカードグリッド表示 */}
            <section id="section-特別企画" className={Styles.categorySection}>
              <h2 className={Styles.categorySection__title}>特別企画</h2>
              {categorizedEvents && categorizedEvents['特別企画']?.length > 0 ? (
                <div className={Styles.categorySection__grid}>
                  {categorizedEvents['特別企画'].map((event) => (
                    <div
                      key={event.id}
                      className={Styles.categorySection__card}
                      onClick={() => handleSelectEvent(event)}
                    >
                      <div className={Styles.categorySection__imageWrapper}>
                        <div
                          className={Styles.categorySection__imageBg}
                          style={{
                            backgroundImage: `url(${optimizeImage(event.event_image.url, { width: 800, quality: 75 })})`,
                          }}
                        />
                        <img
                          src={optimizeImage(event.event_image.url, { width: 800, quality: 80 })}
                          alt={event.event_title}
                          className={Styles.categorySection__image}
                        />
                      </div>
                      <div className={Styles.categorySection__content}>
                        <h3 className={Styles.categorySection__eventTitle}>{event.event_title}</h3>
                        <p className={Styles.categorySection__location}>{event.event_location}</p>
                        <div className={Styles.categorySection__footer}>
                          <div className={Styles.categorySection__dateTags}>
                            {event.event_day.map((day, index) => (
                              <span key={index} className={Styles.categorySection__dateTag}>
                                #{day}
                              </span>
                            ))}
                            {event.school?.map((schoolName: string, index: number) => (
                              <span
                                key={`school-${index}`}
                                className={Styles.categorySection__dateTag}
                              >
                                #{schoolName}
                              </span>
                            ))}
                          </div>
                          <svg
                            width="25"
                            height="25"
                            viewBox="0 0 25 25"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={Styles.categorySection__arrow}
                          >
                            <circle
                              cx="12.5"
                              cy="12.5"
                              r="12.5"
                              transform="rotate(90 12.5 12.5)"
                              fill="#0946F1"
                            />
                            <path
                              d="M7.30859 12.5137L16.833 12.6504"
                              stroke="white"
                              strokeLinecap="round"
                            />
                            <path
                              d="M12.5625 8.8457L17.3071 12.6413"
                              stroke="white"
                              strokeLinecap="round"
                            />
                            <path
                              d="M12.5625 16.4441L17.3071 12.6484"
                              stroke="white"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={Styles.comingSoon}>
                  <p className={Styles.comingSoon__text}>Coming Soon...</p>
                </div>
              )}
            </section>

            <section id="section-ステージ" className={Styles.categorySection}>
              <h2 className={Styles.categorySection__title}>ステージ</h2>
              {categorizedEvents && categorizedEvents['ステージ']?.length > 0 ? (
                <div className={Styles.categorySection__grid}>
                  {categorizedEvents['ステージ'].map((event) => (
                    <div
                      key={event.id}
                      className={Styles.categorySection__card}
                      onClick={() => handleSelectEvent(event)}
                    >
                      <div className={Styles.categorySection__imageWrapper}>
                        <div
                          className={Styles.categorySection__imageBg}
                          style={{
                            backgroundImage: `url(${optimizeImage(event.event_image.url, { width: 800, quality: 75 })})`,
                          }}
                        />
                        <img
                          src={optimizeImage(event.event_image.url, { width: 800, quality: 80 })}
                          alt={event.event_title}
                          className={Styles.categorySection__image}
                        />
                      </div>
                      <div className={Styles.categorySection__content}>
                        <h3 className={Styles.categorySection__eventTitle}>{event.event_title}</h3>
                        <p className={Styles.categorySection__location}>{event.event_location}</p>
                        <div className={Styles.categorySection__footer}>
                          <div className={Styles.categorySection__dateTags}>
                            {event.event_day.map((day: string, index: number) => (
                              <span key={index} className={Styles.categorySection__dateTag}>
                                #{day}
                              </span>
                            ))}
                            {event.school?.map((schoolName: string, index: number) => (
                              <span
                                key={`school-${index}`}
                                className={Styles.categorySection__dateTag}
                              >
                                #{schoolName}
                              </span>
                            ))}
                          </div>
                          <svg
                            width="25"
                            height="25"
                            viewBox="0 0 25 25"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={Styles.categorySection__arrow}
                          >
                            <circle
                              cx="12.5"
                              cy="12.5"
                              r="12.5"
                              transform="rotate(90 12.5 12.5)"
                              fill="#0946F1"
                            />
                            <path
                              d="M7.30859 12.5137L16.833 12.6504"
                              stroke="white"
                              strokeLinecap="round"
                            />
                            <path
                              d="M12.5625 8.8457L17.3071 12.6413"
                              stroke="white"
                              strokeLinecap="round"
                            />
                            <path
                              d="M12.5625 16.4441L17.3071 12.6484"
                              stroke="white"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={Styles.comingSoon}>
                  <p className={Styles.comingSoon__text}>Coming Soon...</p>
                </div>
              )}
            </section>

            <section id="section-サークル/有志" className={Styles.categorySection}>
              <h2 className={Styles.categorySection__title}>サークル/有志</h2>
              {categorizedEvents &&
              categorizedEvents['サークル/有志'] &&
              categorizedEvents['サークル/有志'].length > 0 ? (
                <div className={Styles.categorySection__grid}>
                  {categorizedEvents['サークル/有志'].map((event) => (
                    <div
                      key={event.id}
                      className={Styles.categorySection__card}
                      onClick={() => handleSelectEvent(event)}
                    >
                      <div className={Styles.categorySection__imageWrapper}>
                        <div
                          className={Styles.categorySection__imageBg}
                          style={{
                            backgroundImage: `url(${optimizeImage(event.event_image.url, { width: 800, quality: 75 })})`,
                          }}
                        />
                        <img
                          src={optimizeImage(event.event_image.url, { width: 800, quality: 80 })}
                          alt={event.event_title}
                          className={Styles.categorySection__image}
                        />
                      </div>
                      <div className={Styles.categorySection__content}>
                        <h3 className={Styles.categorySection__eventTitle}>{event.event_title}</h3>
                        <p className={Styles.categorySection__location}>{event.event_location}</p>
                        <div className={Styles.categorySection__footer}>
                          <div className={Styles.categorySection__dateTags}>
                            {event.event_day.map((day: string, index: number) => (
                              <span key={index} className={Styles.categorySection__dateTag}>
                                #{day}
                              </span>
                            ))}
                            {event.school?.map((schoolName: string, index: number) => (
                              <span
                                key={`school-${index}`}
                                className={Styles.categorySection__dateTag}
                              >
                                #{schoolName}
                              </span>
                            ))}
                          </div>
                          <svg
                            width="25"
                            height="25"
                            viewBox="0 0 25 25"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={Styles.categorySection__arrow}
                          >
                            <circle
                              cx="12.5"
                              cy="12.5"
                              r="12.5"
                              transform="rotate(90 12.5 12.5)"
                              fill="#0946F1"
                            />
                            <path
                              d="M7.30859 12.5137L16.833 12.6504"
                              stroke="white"
                              strokeLinecap="round"
                            />
                            <path
                              d="M12.5625 8.8457L17.3071 12.6413"
                              stroke="white"
                              strokeLinecap="round"
                            />
                            <path
                              d="M12.5625 16.4441L17.3071 12.6484"
                              stroke="white"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={Styles.comingSoon}>
                  <p className={Styles.comingSoon__text}>Coming Soon...</p>
                </div>
              )}
            </section>

            <section id="section-その他" className={Styles.categorySection}>
              <h2 className={Styles.categorySection__title}>その他</h2>
              {categorizedEvents &&
              categorizedEvents['その他'] &&
              categorizedEvents['その他'].length > 0 ? (
                <div className={Styles.categorySection__grid}>
                  {categorizedEvents['その他'].map((event) => (
                    <div
                      key={event.id}
                      className={Styles.categorySection__card}
                      onClick={() => handleSelectEvent(event)}
                    >
                      <div className={Styles.categorySection__imageWrapper}>
                        <div
                          className={Styles.categorySection__imageBg}
                          style={{
                            backgroundImage: `url(${optimizeImage(event.event_image.url, { width: 800, quality: 75 })})`,
                          }}
                        />
                        <img
                          src={optimizeImage(event.event_image.url, { width: 800, quality: 80 })}
                          alt={event.event_title}
                          className={Styles.categorySection__image}
                        />
                      </div>
                      <div className={Styles.categorySection__content}>
                        <h3 className={Styles.categorySection__eventTitle}>{event.event_title}</h3>
                        <p className={Styles.categorySection__location}>{event.event_location}</p>
                        <div className={Styles.categorySection__footer}>
                          <div className={Styles.categorySection__dateTags}>
                            {event.event_day.map((day: string, index: number) => (
                              <span key={index} className={Styles.categorySection__dateTag}>
                                #{day}
                              </span>
                            ))}
                            {event.school?.map((schoolName: string, index: number) => (
                              <span
                                key={`school-${index}`}
                                className={Styles.categorySection__dateTag}
                              >
                                #{schoolName}
                              </span>
                            ))}
                          </div>
                          <svg
                            width="25"
                            height="25"
                            viewBox="0 0 25 25"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={Styles.categorySection__arrow}
                          >
                            <circle
                              cx="12.5"
                              cy="12.5"
                              r="12.5"
                              transform="rotate(90 12.5 12.5)"
                              fill="#0946F1"
                            />
                            <path
                              d="M7.30859 12.5137L16.833 12.6504"
                              stroke="white"
                              strokeLinecap="round"
                            />
                            <path
                              d="M12.5625 8.8457L17.3071 12.6413"
                              stroke="white"
                              strokeLinecap="round"
                            />
                            <path
                              d="M12.5625 16.4441L17.3071 12.6484"
                              stroke="white"
                              strokeLinecap="round"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={Styles.comingSoon}>
                  <p className={Styles.comingSoon__text}>Coming Soon...</p>
                </div>
              )}
            </section>
          </div>
        )}

        <section id="all-events" className={Styles.allEventsSection}>
          <h2 className={Styles.allEventsSection__title}>全部見る</h2>
          {eventsByDate.length > 0 ? (
            eventsByDate.map((group) => (
              <div key={group.date} className={Styles.allEventsSection__dateGroup}>
                <h3 className={Styles.allEventsSection__dateTitle}>{group.date}</h3>
                <div className={Styles.allEventsSection__grid}>
                  {group.events.map((event) => (
                    <div
                      key={event.id}
                      className={Styles.allEventsSection__item}
                      onClick={() => handleSelectEvent(event)}
                    >
                      <div className={Styles.allEventsSection__imageWrapper}>
                        <div
                          className={Styles.allEventsSection__imageBg}
                          style={{
                            backgroundImage: `url(${optimizeImage(event.event_image.url, { width: 600, quality: 75 })})`,
                          }}
                        />
                        <img
                          src={optimizeImage(event.event_image.url, { width: 600, quality: 80 })}
                          alt={event.event_title}
                          className={Styles.allEventsSection__image}
                        />
                      </div>
                      <h3 className={Styles.allEventsSection__eventTitle}>
                        {event.event_title.split('\n').map((line, index) => (
                          <React.Fragment key={index}>
                            {index > 0 && <br />}
                            {line}
                          </React.Fragment>
                        ))}
                      </h3>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className={Styles.comingSoon}>
              <p className={Styles.comingSoon__text}>Coming Soon...</p>
            </div>
          )}
        </section>
        <EventFilterNav />
      </main>
      <Footer />
      <FloatingNav />
      {/* イベント詳細モーダル */}
      {selectedEvent &&
        createPortal(
          <div
            className={Styles.modal}
            onClick={handleCloseModal}
            onTouchMove={(e) => e.preventDefault()}
          >
            <div className={Styles.modal__content} onClick={(e) => e.stopPropagation()}>
              <EventCard
                events={[convertEventToEventData(selectedEvent)]}
                onClose={handleCloseModal}
                className={Styles.eventModal}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default Events2025;
